import * as React from 'react';
import type { TenantOption } from '@zafroi/ui-kit';
import { useAuth } from '@/services/auth-context';

/**
 * Tenant context — derived from the authenticated user's session.
 *
 * Before JWT/SSO landed (2026-05-24), this held a hardcoded FALLBACK_TENANTS
 * list because the engage-app had no real session. Now it's a thin wrapper
 * over useAuth().user — the runtime issues a per-tenant JWT, and that's
 * the only tenant the user can act on. Multi-tenant memberships will need
 * a separate /me/memberships endpoint and a real switcher.
 *
 * Kept as a separate public API (useTenant()) so existing consumers don't
 * have to change.
 */

interface TenantContextValue {
  tenants: TenantOption[];
  currentTenant: TenantOption;
  setCurrentTenant: (tenant: TenantOption) => void;
}

const TenantContext = React.createContext<TenantContextValue | undefined>(undefined);

// Shown when no user is signed in yet (e.g. while AuthProvider is still
// rehydrating on boot). Replaced with the real tenant the moment the
// /auth/me response lands.
const UNKNOWN_TENANT: TenantOption = {
  id: 'unknown',
  name: '…',
  code: '—',
};

export function TenantProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();

  const currentTenant: TenantOption = user
    ? {
        id: user.tenantId,
        name: user.tenantName,
        code: user.tenantCode,
      }
    : UNKNOWN_TENANT;

  const value = React.useMemo<TenantContextValue>(
    () => ({
      tenants: [currentTenant],
      currentTenant,
      // Single-tenant for now — see "JWT/SSO between Ops and Engage" in
      // docs/BACKLOG.md and the follow-up "multi-tenant memberships" work.
      setCurrentTenant: () => {
        /* no-op */
      },
    }),
    [currentTenant],
  );

  return <TenantContext.Provider value={value}>{children}</TenantContext.Provider>;
}

export function useTenant(): TenantContextValue {
  const ctx = React.useContext(TenantContext);
  if (!ctx) throw new Error('useTenant must be used inside a TenantProvider');
  return ctx;
}
